import React, { useState, useEffect } from "react";
import Content from "../components/Content";
import Layout from "../containers/LayoutAdministrativo";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ControlPointDuplicateIcon from "@mui/icons-material/ControlPointDuplicate";
import RemoveIcon from "@mui/icons-material/Remove";
import PaidIcon from "@mui/icons-material/Paid";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import Icon from "@mdi/react";
import { mdiCashMultiple } from "@mdi/js";
import { mdiFileExcel } from "@mdi/js";
import {
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Typography,
  OutlinedInput,
  Stack,
  Button,
  Box,
  IconButton,
  Tooltip,
  tooltipClasses,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useNavigate } from "react-router-dom";

// Services
import SucursalesService from "../services/SucursalesService";
import ConveniosService from "../services/ConveniosService";
import SolicitudesService from "../services/SolicitudesService";
import CajaService from "../services/CajaService";

const MySwal = withReactContent(Swal);

const CuentasCobrar = () => {
  const navigate = useNavigate();

  // Loading and list states
  const [loading, setLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);

  const getFechaHoy = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  // Filter states
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroSucursal, setFiltroSucursal] = useState("Todas");
  const [filtroEmpresa, setFiltroEmpresa] = useState("Todas");
  const [filtroEstadoPago, setFiltroEstadoPago] = useState("Todos");
  const [fechaInicial, setFechaInicial] = useState(getFechaHoy());
  const [fechaFinal, setFechaFinal] = useState(getFechaHoy());

  // Checkbox selection
  const [selectedIds, setSelectedIds] = useState([]);

  // User session
  const usuarioString = sessionStorage.getItem("usuario");
  const currentU = JSON.parse(usuarioString || "{}");
  const viewOnly = false;

  useEffect(() => {
    document.title = "Cuentas por Cobrar";
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sucursalesRes, conveniosRes, solicitudesRes, cajaRes] = await Promise.all([
        SucursalesService.getSucursales(),
        ConveniosService.getConvenios(),
        SolicitudesService.getSolicitudes(),
        CajaService.getCaja()
      ]);

      const sucs = sucursalesRes?.data?.sucursales || [];
      const convs = conveniosRes?.data?.convenios || [];
      const sols = solicitudesRes?.data?.solicitudes || [];
      const cajas = cajaRes?.data?.caja || [];

      // Map caja records for quick lookup
      const cajaMap = {};
      cajas.forEach((c) => {
        cajaMap[c.idSolicitud] = c;
      });

      // Filter and merge
      const merged = sols
        .filter((s) => {
          // Show only solicitudes with an associated convenio/empresa
          if (!s.idConvenio) return false;
          // Hide cancelled solicitudes
          const isCancelled = s.estatus === "C" || (s.motivoCancelacion && s.motivoCancelacion.trim() !== "");
          if (isCancelled) return false;
          return true;
        })
        .map((s) => {
          const cajaRec = cajaMap[s.idSolicitud];
          return {
            ...s,
            adeudo: Number(cajaRec?.adeudo ?? s.totalPagar ?? 0),
            total: Number(cajaRec?.subtotal ?? s.totalPagar ?? 0),
            pagado: Number(cajaRec?.pagado ?? 0),
            cajaRec
          };
        });

      setSucursales(sucs);
      setEmpresas(convs);
      setSolicitudes(merged);
    } catch (err) {
      console.error("Error fetching data for accounts receivable:", err);
      mostrarError("No se pudieron cargar los datos de cuentas por cobrar");
    } finally {
      setLoading(false);
    }
  };

  // Helper formatting functions
  const formatEstadoPago = (est) => {
    if (est === "S") return "Sin Pagar";
    if (est === "P") return "Pagado";
    if (est === "PP") return "Parcialmente Pagado";
    if (est === "C") return "Cancelado";
    return est || "";
  };

  const getPaymentState = (s) => {
    if (s.adeudo <= 0) return "Pagado";
    if (s.pagado > 0 && s.adeudo > 0) return "parcialmente pagado";
    return "sin pagar";
  };

  const formatEstadoResultados = (estado) => {
    if (estado === "E") return "En Proceso";
    if (estado === "R") return "Reportado";
    if (estado === "V") return "Revisado";
    if (estado === "L") return "Liberado";
    if (estado === "EN") return "Entregado";
    if (estado === "RP") return "Resultados Parciales";
    return estado || "";
  };

  const getEstadoPagoStyle = (est) => {
    if (est === "P" || est === "Pagado") return { color: "#2e7d32", fontWeight: "bold" };
    if (est === "PP" || est === "Parcialmente Pagado" || est === "parcialmente pagado") return { color: "#ed6c02", fontWeight: "bold" };
    return { color: "#d32f2f", fontWeight: "bold" };
  };

  const getEstadoResultadosStyle = (est) => {
    if (["L", "EN", "Liberado", "Entregado"].includes(est)) return { color: "#2e7d32", fontWeight: "bold" };
    if (["R", "Reportado"].includes(est)) return { color: "#ed6c02", fontWeight: "bold" };
    if (["V", "Revisado"].includes(est)) return { color: "#1976d2", fontWeight: "bold" };
    return { color: "black", fontWeight: "bold" };
  };

  function getFormattedNow() {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hours}:${minutes}`;
  }

  function mostrarMensaje(mensaje) {
    Swal.fire({
      title: "",
      text: mensaje,
      icon: "success",
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#006787"
    });
  }

  const mostrarError = (mensaje) => {
    Swal.fire({
      title: "¡Error!",
      text: mensaje,
      icon: "error",
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#006787"
    });
  };

  const getTimeStampSolicitud = (sol) => {
    const fStr = sol.fecha || sol.fechaSolicitud || sol.fechaCreacion || sol.createdAt || sol.created_at;
    if (fStr) {
      const dateVal = new Date(typeof fStr === "string" ? fStr.replace(/-/g, "/") : fStr);
      if (!Number.isNaN(dateVal.getTime())) return dateVal.getTime();
    }
    return Number(sol.idSolicitud || sol.id || sol.orden || 0);
  };

  // Filter application
  const solicitudesFiltradas = solicitudes
    .filter((s) => {
      // 1. Text Search (Request code or patient name)
      const pacNombre = `${s.nombrePaciente || ""} ${s.apPaternoPaciente || ""} ${s.apMaternoPaciente || ""}`.trim().toLowerCase();
      const matchesTexto =
        filtroTexto.trim() === "" ||
        (s.codigo && s.codigo.toLowerCase().includes(filtroTexto.toLowerCase())) ||
        pacNombre.includes(filtroTexto.toLowerCase());

      if (!matchesTexto) return false;

      // 2. Sucursal filter
      if (filtroSucursal !== "Todas" && String(s.idSucursal) !== String(filtroSucursal)) {
        return false;
      }

      // 3. Empresa/Convenio filter
      if (filtroEmpresa !== "Todas" && String(s.idConvenio) !== String(filtroEmpresa)) {
        return false;
      }

      // 4. Payment status filter
      const estadoPagoActual = getPaymentState(s);
      if (
        filtroEstadoPago !== "Todos" &&
        estadoPagoActual.toLowerCase() !== filtroEstadoPago.toLowerCase()
      ) {
        return false;
      }

      // 5. Date filter
      if (s.fecha) {
        const reqDate = s.fecha.substring(0, 10);
        if (fechaInicial && reqDate < fechaInicial) return false;
        if (fechaFinal && reqDate > fechaFinal) return false;
      }

      return true;
    })
    .sort((a, b) => {
      const timeA = getTimeStampSolicitud(a);
      const timeB = getTimeStampSolicitud(b);
      if (timeB !== timeA) return timeB - timeA;
      return Number(b.idSolicitud || b.orden || 0) - Number(a.idSolicitud || a.orden || 0);
    });

  // Checkbox functions
  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allSelectable = solicitudesFiltradas.filter((s) => s.adeudo > 0).map((s) => s.idSolicitud);
      setSelectedIds(allSelectable);
    } else {
      setSelectedIds([]);
    }
  };

  // payment action
  const handlePagarSeleccionados = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({
        title: "Atención",
        text: "Debe seleccionarse al menos una solicitud",
        icon: "warning",
        confirmButtonColor: "#006787"
      });
      return;
    }

    const selectedSols = solicitudes.filter((s) => selectedIds.includes(s.idSolicitud));
    const totalPagar = selectedSols.reduce((acc, s) => acc + s.adeudo, 0);

    const sucursalOptions = sucursales
      .map(
        (suc) =>
          `<option value="${suc.idSucursal}" ${String(suc.idSucursal) === String(currentU.idSucursal || sucursales[0]?.idSucursal) ? "selected" : ""
          }>${suc.nombre}</option>`
      )
      .join("");

    const { value: datosPago } = await MySwal.fire({
      title: "PAGAR CUENTAS SELECCIONADAS",
      width: "55%",
      customClass: {
        title: "tituloDegradado",
        popup: "swalMaxHeight",
        confirmButton: "btnConfirmGreen",
      },
      html: `
        <div style="display:flex;flex-direction:column;gap:1vw;text-align:left;">
          <div class="tituloDegradado" style="font-size:1rem;padding:0.55vw 0.7vw;border-radius:8px;display:block;width:100%;text-align:center;font-weight:700;">TOTAL A PAGAR</div>
          <div style="font-size:1.7rem;padding:0.3vw 0.5vw;border-radius:8px;display:block;text-align:center;font-weight:800;color:#18234f;">$${totalPagar.toFixed(2)}</div>
          
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:1rem;margin-bottom:0.5vw;">
            <div style="display:flex;flex-direction:column;gap:0.35rem;">
              <label for="swal-sucursalPago" style="font-weight:700;font-size:0.95vw;">Sucursal de Registro:</label>
              <select id="swal-sucursalPago" style="width:100%;padding:0.7vw 0.8vw;font-size:0.95vw;border:1px solid #cfd8dc;border-radius:6px;background-color:white;">
                ${sucursalOptions}
              </select>
            </div>
            
            <div style="display:flex;flex-direction:column;gap:0.35rem;">
              <label for="swal-formaPago" style="font-weight:700;font-size:0.95vw;">Forma de Pago:</label>
              <select id="swal-formaPago" style="width:100%;padding:0.7vw 0.8vw;font-size:0.95vw;border:1px solid #cfd8dc;border-radius:6px;background-color:white;">
                <option value="efectivo">Efectivo</option>
                <option value="debito">Tarjeta de débito</option>
                <option value="credito">Tarjeta de crédito</option>
                <option value="cheque">Cheque</option>
                <option value="transferencia">Transferencia</option>
              </select>
            </div>

            <div id="swal-ultimos4-group" style="display:none;flex-direction:column;gap:0.35rem;">
              <label for="swal-ultimos4" style="font-weight:700;font-size:0.95vw;">Últimos 4 dígitos:</label>
              <input id="swal-ultimos4" class="input-swal" placeholder="0000" maxlength="4" inputmode="numeric" style="width:100%;padding:0.7vw 0.8vw;font-size:0.95vw;border:1px solid #cfd8dc;border-radius:6px;" />
            </div>

            <div style="display:flex;flex-direction:column;gap:0.35rem;">
              <label for="swal-cantidad" style="font-weight:700;font-size:0.95vw;">Cantidad recibida:</label>
              <input id="swal-cantidad" class="input-swal" placeholder="0.00" type="number" step="0.01" style="width:100%;padding:0.7vw 0.8vw;font-size:0.95vw;border:1px solid #cfd8dc;border-radius:6px;" />
            </div>
          </div>

          <div style="display:flex;flex-direction:column;align-items:center;gap:0.25rem;justify-content:center;max-width:220px;margin:0 auto;width:100%;">
            <label for="swal-cambio" style="font-weight:700;font-size:0.8vw;">Cambio:</label>
            <div id="swal-cambio" style="min-height:2rem;display:flex;align-items:center;justify-content:center;padding:0.45vw 0.6vw;font-size:0.85vw;border:1px solid #cfd8dc;border-radius:6px;background:#f7f9fc;font-weight:700;color:#18234f;width:100%;"> </div>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Pagar",
      cancelButtonText: "Cancelar",
      didOpen: () => {
        const select = document.getElementById("swal-formaPago");
        const ultimos4Group = document.getElementById("swal-ultimos4-group");
        const cantidadInput = document.getElementById("swal-cantidad");
        const cambioLabel = document.getElementById("swal-cambio");

        const actualizarCambio = () => {
          const recibido = parseFloat(cantidadInput.value) || 0;
          const cambio = recibido - totalPagar;
          cambioLabel.textContent = cambio > 0 ? `$${cambio.toFixed(2)}` : "";
        };

        const actualizarUltimos4 = () => {
          const value = select.value;
          ultimos4Group.style.display = ["debito", "credito", "cheque", "transferencia"].includes(value) ? "flex" : "none";
        };

        select.addEventListener("change", () => {
          actualizarUltimos4();
          actualizarCambio();
        });

        cantidadInput.addEventListener("input", actualizarCambio);
        actualizarUltimos4();
        actualizarCambio();
      },
      preConfirm: () => {
        const sucursalPago = document.getElementById("swal-sucursalPago")?.value;
        const formaPago = document.getElementById("swal-formaPago")?.value?.trim();
        const ultimos4 = document.getElementById("swal-ultimos4")?.value?.trim();
        const cantidad = parseFloat(document.getElementById("swal-cantidad")?.value);

        if (!formaPago || Number.isNaN(cantidad)) {
          Swal.showValidationMessage("La forma de pago y la cantidad son obligatorias");
          return false;
        }

        if (cantidad < totalPagar) {
          Swal.showValidationMessage("No se puede dar abono, se debe pagar el total");
          return false;
        }

        return { sucursalPago, formaPago, ultimos4, cantidad };
      }
    });

    if (datosPago) {
      setLoading(true);
      try {
        let formaPagoN = datosPago.formaPago;
        if (datosPago.formaPago === "debito" || datosPago.formaPago === "credito") {
          formaPagoN = "tarjeta";
        }

        // Loop over each request and pay it
        for (const sol of selectedSols) {
          const pagoBody = {
            formaPago: formaPagoN,
            ultimos4: datosPago.ultimos4 || "",
            cantidad: sol.adeudo, // Pay exactly the outstanding debt of this request
            idSolicitud: sol.idSolicitud,
            cobro: currentU.id,
            fechaCobro: getFormattedNow()
          };
          await CajaService.pagarSolicitud(pagoBody);
        }

        mostrarMensaje("Pagos registrados correctamente");
        setSelectedIds([]);
        fetchData();
      } catch (err) {
        console.error(err);
        mostrarError("Ocurrió un error al procesar el pago masivo.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Excel Export
  const handleExportarExcel = () => {
    const fechaActual = new Date().toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });

    let tableHtml = `<table border="1">`;
    tableHtml += `<tr><td colSpan="10" style="font-weight: bold; font-size: 14pt; height: 30px; text-align: center;">Reporte de Cuentas por Cobrar a Empresas - ${fechaActual}</td></tr>`;

    tableHtml += `<tr style="background-color: #e0f2f1; font-weight: bold; text-align: center;">`;
    tableHtml += `<td>#</td>`;
    tableHtml += `<td>Código</td>`;
    tableHtml += `<td>Sucursal</td>`;
    tableHtml += `<td>Fecha solicitud</td>`;
    tableHtml += `<td>Empresa</td>`;
    tableHtml += `<td>Paciente</td>`;
    tableHtml += `<td>Estado solicitud</td>`;
    tableHtml += `<td>Estado resultados</td>`;
    tableHtml += `<td>Total solicitud</td>`;
    tableHtml += `<td>Total pagar (Adeudo)</td>`;
    tableHtml += `</tr>`;

    solicitudesFiltradas.forEach((sol, idx) => {
      const pacNombre = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
      tableHtml += `<tr>`;
      tableHtml += `<td>${idx + 1}</td>`;
      tableHtml += `<td>${sol.codigo || ""}</td>`;
      tableHtml += `<td>${sol.nombreSucursal || ""}</td>`;
      tableHtml += `<td>${sol.fecha || ""}</td>`;
      tableHtml += `<td>${sol.nombreConvenio || ""}</td>`;
      tableHtml += `<td>${pacNombre}</td>`;
      tableHtml += `<td>${formatEstadoPago(sol.estatus)}</td>`;
      tableHtml += `<td>${formatEstadoResultados(sol.estatusResultados)}</td>`;
      tableHtml += `<td style="text-align: right;">${sol.total?.toFixed(2) ?? "0.00"}</td>`;
      tableHtml += `<td style="text-align: right;">${sol.adeudo?.toFixed(2) ?? "0.00"}</td>`;
      tableHtml += `</tr>`;
    });

    tableHtml += `</table>`;

    const excelFile = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
      <meta charset="utf-8" />
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>Cuentas Cobrar</x:Name>
              <x:WorksheetOptions>
                <x:DisplayGridlines/>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      </head>
      <body>
      ${tableHtml}
      </body>
      </html>
    `;

    const blob = new Blob([excelFile], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Cuentas_Cobrar_${new Date().toISOString().slice(0, 10)}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleLimpiarFiltros = () => {
    setFiltroTexto("");
    setFiltroSucursal("Todas");
    setFiltroEmpresa("Todas");
    setFiltroEstadoPago("Todos");
    setFechaInicial(getFechaHoy());
    setFechaFinal(getFechaHoy());
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            width: "100%",
            paddingTop: "2vh",
            paddingBottom: "2vh",
          }}
        >
          <Stack spacing={2} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            {/* Filter Panel */}
            <Paper elevation={3} sx={{ p: 2, borderRadius: 4, width: "100%", boxSizing: "border-box" }} className="paperFiltrosCuentasC">
              <Grid item xs={12}>
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 2 }}>
                  <Typography
                    variant="h9"
                    gutterBottom
                    sx={{
                      background: "linear-gradient(90deg, #006787, #014e65)",
                      color: "white",
                      padding: "0.5vw 1vw",
                      borderRadius: "8px",
                      display: "inline-block",
                      textAlign: "center",
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      CUENTAS POR COBRAR A EMPRESAS
                    </Box>
                  </Typography>
                </Box>
              </Grid>

              <Grid container spacing={"0.3vw"}>
                <Grid
                  item
                  xs={12}
                  container
                  spacing={"1vw"}
                  sx={{
                    paddingTop: "0.6vw",
                    width: "100%",
                    display: "flex",
                  }}
                >
                  <Stack direction="row" spacing={"3vw"} sx={{ width: "100%" }}>
                    <Grid item xs={12} sx={{ width: "100%" }}>
                      <Table sx={{ width: "100%", backgroundColor: "white" }}>
                        <TableHead sx={{ backgroundColor: "#18234f" }}>
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ fontSize: "1.5vw", color: "white", fontWeight: "bold", borderBottom: "none" }}>
                              Filtros
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody sx={{ "& .MuiTableCell-root": { padding: "4px 6px" } }}>
                          {/* Search patient or request code */}
                          <TableRow>
                            <TableCell align="right" sx={{ width: "40%", fontWeight: "bold" }}>
                              Coloque el código de la solicitud o nombre del paciente:
                            </TableCell>
                            <TableCell sx={{ width: "30%" }}>
                              <TextField
                                fullWidth
                                disabled={viewOnly}
                                value={filtroTexto}
                                onChange={(e) => setFiltroTexto(e.target.value)}
                                sx={{
                                  width: "18vw",
                                  "& .MuiInputBase-root": { height: "5vh" },
                                  "& .MuiInputBase-input": { fontSize: "1vw" },
                                }}
                              />
                            </TableCell>
                            <TableCell colSpan={2}>
                              <Button
                                size="medium"
                                variant="contained"
                                onClick={handleLimpiarFiltros}
                                sx={{ backgroundColor: "#18234f", color: "white", textTransform: "none", fontSize: "1vw" }}
                              >
                                Mostrar todos
                              </Button>
                            </TableCell>
                          </TableRow>

                          {/* Sucursal selection */}
                          <TableRow>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                              Buscar Sucursal:
                            </TableCell>
                            <TableCell colSpan={3}>
                              <Select
                                value={filtroSucursal}
                                onChange={(e) => setFiltroSucursal(e.target.value)}
                                input={<OutlinedInput />}
                                sx={{
                                  width: "18vw",
                                  height: "5vh",
                                  "& .MuiSelect-select": {
                                    padding: "0 0.5vw !important",
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "1vw",
                                  },
                                }}
                              >
                                <MenuItem value="Todas">Todas</MenuItem>
                                {sucursales.map((suc) => (
                                  <MenuItem key={suc.idSucursal} value={suc.idSucursal} sx={{ fontSize: "1vw" }}>
                                    {suc.nombre}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                          </TableRow>

                          {/* Empresa selection */}
                          <TableRow>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                              Seleccionar empresa:
                            </TableCell>
                            <TableCell colSpan={3}>
                              <Select
                                value={filtroEmpresa}
                                onChange={(e) => setFiltroEmpresa(e.target.value)}
                                input={<OutlinedInput />}
                                sx={{
                                  width: "18vw",
                                  height: "5vh",
                                  "& .MuiSelect-select": {
                                    padding: "0 0.5vw !important",
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "1vw",
                                  },
                                }}
                              >
                                <MenuItem value="Todas">Todas</MenuItem>
                                {empresas.map((emp) => (
                                  <MenuItem key={emp.idConvenio} value={emp.idConvenio} sx={{ fontSize: "1vw" }}>
                                    {emp.nombre}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                          </TableRow>

                          {/* Payment Status selection */}
                          <TableRow>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                              Estado de pago:
                            </TableCell>
                            <TableCell colSpan={3}>
                              <Select
                                value={filtroEstadoPago}
                                onChange={(e) => setFiltroEstadoPago(e.target.value)}
                                input={<OutlinedInput />}
                                sx={{
                                  width: "18vw",
                                  height: "5vh",
                                  "& .MuiSelect-select": {
                                    padding: "0 0.5vw !important",
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "1vw",
                                  },
                                }}
                              >
                                <MenuItem value="Todos">Todos</MenuItem>
                                <MenuItem value="sin pagar">Sin Pagar</MenuItem>
                                <MenuItem value="parcialmente pagado">Parcialmente Pagado</MenuItem>
                                <MenuItem value="Pagado">Pagado</MenuItem>
                              </Select>
                            </TableCell>
                          </TableRow>

                          {/* Dates range */}
                          <TableRow>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                              Seleccione fecha inicial y final:
                            </TableCell>
                            <TableCell colSpan={3}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                  type="date"
                                  value={fechaInicial}
                                  onChange={(e) => setFechaInicial(e.target.value)}
                                  sx={{
                                    width: "10vw",
                                    "& .MuiInputBase-root": { height: "5vh" },
                                    "& .MuiInputBase-input": { fontSize: "1vw" },
                                  }}
                                />
                                <Typography sx={{ fontSize: "1vw" }}>a</Typography>
                                <TextField
                                  type="date"
                                  value={fechaFinal}
                                  onChange={(e) => setFechaFinal(e.target.value)}
                                  sx={{
                                    width: "10vw",
                                    "& .MuiInputBase-root": { height: "5vh" },
                                    "& .MuiInputBase-input": { fontSize: "1vw" },
                                  }}
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>

                          {/* Excel Export Row */}
                          <TableRow>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                              Exportar Excel:
                            </TableCell>
                            <TableCell colSpan={3}>
                              <IconButton onClick={handleExportarExcel} sx={{ color: "#006787", fontSize: "0.9vw", borderRadius: "4px", p: "0.3vw" }}>
                                <Icon path={mdiFileExcel} size={1} color="green" />
                                <Typography sx={{ ml: 0.5, fontSize: "1vw", fontWeight: "bold", color: "green" }}>Exportar</Typography>
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* Results Table Panel */}
            <Paper elevation={3} sx={{ p: 2, borderRadius: 4, width: "100%", boxSizing: "border-box" }} className="paperTablaCuentasC">
              {loading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6 }}>
                  <CircularProgress sx={{ color: "#006787" }} />
                  <Typography sx={{ mt: 2, fontWeight: "bold", color: "#006787", fontSize: "0.9vw" }}>
                    Cargando cuentas por cobrar...
                  </Typography>
                </Box>
              ) : (
                <Table sx={{ backgroundColor: "white", width: "100%", "& .MuiTableCell-root": { padding: "4px 8px" } }}>
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        #
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        Código
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        Sucursal
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        Fecha solicitud
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        Empresa
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        Paciente
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        Estado pago
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        Estado resultados
                      </TableCell>
                      <TableCell align="right" sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        Total solicitud
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold", borderBottom: "none", fontSize: "1vw", py: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <span>Total pagar</span>
                          <Checkbox
                            size="small"
                            indeterminate={selectedIds.length > 0 && selectedIds.length < solicitudesFiltradas.filter((s) => s.adeudo > 0).length}
                            checked={solicitudesFiltradas.filter((s) => s.adeudo > 0).length > 0 && selectedIds.length === solicitudesFiltradas.filter((s) => s.adeudo > 0).length}
                            onChange={handleSelectAll}
                            sx={{ color: "white", p: 0, mt: 0.5, "&.Mui-checked": { color: "white" }, "&.MuiCheckbox-indeterminate": { color: "white" } }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ "& .MuiTableCell-root": { padding: "8px 10px", fontSize: "1vw" } }}>
                    {solicitudesFiltradas.length > 0 ? (
                      solicitudesFiltradas.map((sol, index) => {
                        const pacNombre = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
                        const isSelected = selectedIds.includes(sol.idSolicitud);
                        const isSelectable = sol.adeudo > 0;

                        return (
                          <TableRow key={sol.idSolicitud} hover>
                            <TableCell sx={{ borderRight: "1px solid #ccc" }}>{index + 1}</TableCell>
                            <TableCell sx={{ borderRight: "1px solid #ccc", fontWeight: "bold" }}>{sol.codigo}</TableCell>
                            <TableCell sx={{ borderRight: "1px solid #ccc" }}>{sol.nombreSucursal}</TableCell>
                            <TableCell sx={{ borderRight: "1px solid #ccc" }}>{sol.fecha}</TableCell>
                            <TableCell sx={{ borderRight: "1px solid #ccc" }}>{sol.nombreConvenio}</TableCell>
                            <TableCell sx={{ borderRight: "1px solid #ccc" }}>{pacNombre}</TableCell>
                            <TableCell sx={{ borderRight: "1px solid #ccc", ...getEstadoPagoStyle(getPaymentState(sol)) }}>
                              {formatEstadoPago(sol.estatus)}
                            </TableCell>
                            <TableCell sx={{ borderRight: "1px solid #ccc", ...getEstadoResultadosStyle(sol.estatusResultados) }}>
                              {formatEstadoResultados(sol.estatusResultados)}
                            </TableCell>
                            <TableCell align="right" sx={{ borderRight: "1px solid #ccc", fontWeight: "bold" }}>
                              ${sol.total?.toFixed(2) ?? "0.00"}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                <Typography sx={{ fontWeight: "bold", fontSize: "1vw" }}>
                                  ${sol.adeudo?.toFixed(2) ?? "0.00"}
                                </Typography>
                                {isSelectable ? (
                                  <Checkbox
                                    size="small"
                                    checked={isSelected}
                                    onChange={() => handleToggleSelect(sol.idSolicitud)}
                                    sx={{ p: 0 }}
                                  />
                                ) : (
                                  <span style={{ fontSize: "0.7vw", color: "green", fontWeight: "bold" }}>Pagado</span>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          No se encontraron solicitudes que coincidan con los filtros.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Floating payment trigger button */}
        {!viewOnly && !loading && (
          <Box
            sx={{
              position: "fixed",
              right: "3vw",
              top: "30%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              zIndex: 1000
            }}
          >
            <Tooltip
              title="Pagar solicitudes seleccionadas"
              arrow
              placement="left"
              slotProps={{
                popper: {
                  sx: {
                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
                      marginTop: "0px",
                    },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]: {
                      marginBottom: "0.2vw",
                    },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]: {
                      marginLeft: "0px",
                    },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                      marginRight: "0px",
                    },
                  },
                },
                tooltip: {
                  sx: {
                    fontSize: "0.75vw",
                    padding: "0.5vw 0.8vw",
                    borderRadius: "8px",
                    backgroundColor: "rgba(255,255,255,0.95)",
                    fontWeight: "bold",
                    color: "#18234f",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.15)"
                  },
                },
                arrow: {
                  sx: {
                    color: "rgba(255,255,255,0.95)",
                  },
                },
              }}
            >
              <IconButton
                onClick={handlePagarSeleccionados}
                disabled={selectedIds.length === 0}
                sx={{
                  backgroundColor: selectedIds.length > 0 ? "#006787" : "#e0e0e0",
                  color: selectedIds.length > 0 ? "#ffffff" : "#9e9e9e",
                  "&:hover": {
                    backgroundColor: selectedIds.length > 0 ? "#014e65" : "#e0e0e0",
                  },
                  width: "3.5vw",
                  height: "3.5vw",
                  boxShadow: "0px 4px 10px rgba(0,0,0,0.2)"
                }}
              >
                <Icon path={mdiCashMultiple} size={1} color={selectedIds.length > 0 ? "white" : "gray"} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Content>
    </Layout>
  );
};

export default CuentasCobrar;
